// src/components/Table/DataTable.tsx
import React, { CSSProperties } from 'react';

export interface ColumnConfig {
  key: string;
  header: string | React.ReactNode;
  label?: string; // Label shown below header
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, item: any, index: number) => React.ReactNode;
  renderHeader?: () => React.ReactNode;
  cellStyle?: CSSProperties;
}

export interface DataTableProps {
  columns: ColumnConfig[];
  data: any[];
  onRowClick?: (item: any, index: number) => void;
  loading?: boolean;
  headerStyle?: CSSProperties;
  rowStyle?: CSSProperties;
  alternateRowColors?: boolean;
  hoverEffect?: boolean;
  containerStyle?: CSSProperties;
  expandedRowId?: string | null;
  renderExpandedContent?: (item: any) => React.ReactNode;
  getRowId?: (item: any) => string;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  onRowClick,
  loading = false,
  headerStyle,
  rowStyle,
  alternateRowColors = true,
  hoverEffect = true,
  containerStyle,
  expandedRowId,
  renderExpandedContent,
  getRowId,
}) => {
  // Calculate grid template columns
  const gridTemplateColumns = columns.map((col) => col.width || '1fr').join(' ');

  const defaultHeaderStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns,
    gap: '1rem',
    padding: '1.5rem',
    backgroundColor: '#202020',
    borderBottom: '2px solid #1b1b1b',
    color: '#fefeff',
    fontSize: '13px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    ...headerStyle,
  };

  const defaultRowStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns,
    gap: '1rem',
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #3a3a3a',
    alignItems: 'center',
    transition: 'background-color 0.2s ease',
    ...rowStyle,
  };

  const getRowBackground = (index: number) => {
    if (!alternateRowColors) return 'transparent';
    return index % 2 === 0 ? '#141414' : '#0f0f0f';
  };

  const getCellAlign = (align?: string) => {
    switch (align) {
      case 'center':
        return { textAlign: 'center' as const, display: 'flex', justifyContent: 'center', alignItems: 'center' };
      case 'right':
        return { textAlign: 'right' as const, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' };
      default:
        return { textAlign: 'left' as const };
    }
  };

  return (
    <div
      style={{
        background: '#1e1e1e',
        borderRadius: '8px',
        overflow: 'hidden',
        ...containerStyle,
      }}
    >
      {/* Table Header */}
      <div style={defaultHeaderStyle}>
        {columns.map((column) => (
          <div key={column.key} style={getCellAlign(column.align)}>
            {column.renderHeader ? column.renderHeader() : column.header}
          </div>
        ))}
      </div>

      {/* Column Labels (Optional) */}
      {columns.some((col) => col.label) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns,
            gap: '1rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#161617',
            fontSize: '12px',
            color: '#666',
            borderBottom: '1px solid #2a2a2a',
          }}
        >
          {columns.map((column) => (
            <div key={`label-${column.key}`} style={getCellAlign(column.align)}>
              {column.label || ''}
            </div>
          ))}
        </div>
      )}

      {/* Table Rows */}
      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF' }}>Loading...</div>
      ) : data.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF' }}>No data available</div>
      ) : (
        data.map((item, idx) => {
          const rowId = getRowId ? getRowId(item) : String(idx);
          const isExpanded = expandedRowId === rowId;
          return (
            <React.Fragment key={idx}>
              <div
                onClick={() => onRowClick?.(item, idx)}
                style={{
                  ...defaultRowStyle,
                  background: getRowBackground(idx),
                  cursor: onRowClick ? 'pointer' : 'default',
                }}
                onMouseEnter={(e) => {
                  if (hoverEffect) {
                    e.currentTarget.style.backgroundColor = '#181717';
                  }
                }}
                onMouseLeave={(e) => {
                  if (hoverEffect) {
                    e.currentTarget.style.backgroundColor = getRowBackground(idx);
                  }
                }}
              >
                {columns.map((column) => (
                  <div
                    key={`${idx}-${column.key}`}
                    style={{
                      ...getCellAlign(column.align),
                      color: '#ffffff',
                      fontSize: '15px',
                      ...column.cellStyle,
                    }}
                  >
                    {column.render ? column.render(item[column.key], item, idx) : item[column.key]}
                  </div>
                ))}
              </div>
              {isExpanded && renderExpandedContent && (
                <div
                  style={{
                    padding: '1rem 1.5rem',
                    backgroundColor: '#2a2a2a',
                    borderBottom: '1px solid #3a3a3a',
                    color: '#d1d5db',
                  }}
                >
                  {renderExpandedContent(item)}
                </div>
              )}
            </React.Fragment>
          );
        })
      )}
    </div>
  );
};

export default DataTable;
